import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NewsletterManagementService } from '../newsletter-management.service';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import {
  CreateRegionRequestDTO,
  ICreateRegionRequest,
} from '../interfaces/create-region-request';
import { FREQUENCY_NAME } from '../constants/frequency-friendly-names';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from 'src/app/shared/services/alert.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { SectionHeaderComponent } from 'src/app/shared/standalone/components/section-header/section-header.component';
import { MatRadioModule } from '@angular/material/radio';
import { CommonModule } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { first, take } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { ICreateNewsletterFormGroup } from '../interfaces/i-create-newsletter-form-group';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';

@Component({
  selector: 'app-add-newsletter',
  templateUrl: './add-newsletter.component.html',
  standalone: true,
  imports: [
    MatFormFieldModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatInputModule,
    MatListModule,
    MatRadioModule,
    SectionHeaderComponent,
  ],
})
export class AddNewsletterComponent implements OnInit {
  availableSites: CreateRegionRequestDTO[];
  submitted: boolean;
  newsletterForm: FormGroup<ICreateNewsletterFormGroup> = this.fb.group({
    regions: this.fb.control([], Validators.required),
    imageUrl: this.fb.control(''),
    displayOnce: this.fb.control(false),
    name: this.fb.control('', Validators.required),
    description: this.fb.control(''),
    special: this.fb.control(false),
    frequency: this.fb.control(null, Validators.required),
    lowerFreqAlt: this.fb.control([]),
    lowerFreqAltDefault: this.fb.control(0),
    createdBy: '',
  });
  availableFrequencies = Object.entries(FREQUENCY_NAME).map(([key, value]) => ({
    key: parseInt(key),
    name: value,
  }));
  constructor(
    private fb: FormBuilder,
    private accountsService: CitywireInternalAccountsService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private router: Router,
    private newsletterManagementService: NewsletterManagementService
  ) {}

  ngOnInit(): void {
    forkJoin([
      this.accountsService.currentUser.pipe(take(1)),
      this.newsletterManagementService.getAllNewsletters$(),
    ])
      .pipe(first())
      .subscribe(([currentUser, allSites]) => {
        this.newsletterForm.patchValue({
          createdBy: currentUser.username.toLowerCase(),
        });

        this.availableSites = allSites
          .filter((site) => site.name) // there are sites with no name
          .map((site) => new CreateRegionRequestDTO(site.region, site.name));
      });
  }

  createNewsletter(selectedSites: MatOption[]) {
    const selectedRegions: ICreateRegionRequest[] = selectedSites.map(
      (option) => option.value
    );

    if (!this.isValidNewsletterForm(selectedRegions)) {
      return;
    }

    this.submitted = true;

    this.newsletterManagementService
      .createNewsletter$(this.newsletterForm.getRawValue())
      .subscribe({
        next: () => this.onCreateSuccess(selectedRegions),
        error: () => this.onCreateError(),
        complete: () => (this.submitted = false),
      });
  }

  isValidNewsletterForm(selectedRegions: ICreateRegionRequest[]): boolean {
    this.newsletterForm.patchValue({ regions: selectedRegions });
    this.newsletterForm.markAllAsTouched();
    return this.newsletterForm.valid;
  }

  onCreateSuccess(selectedRegions: ICreateRegionRequest[]) {
    const firstRegion = selectedRegions[0];
    this.alertService.success(
      `Newsletter: "${
        this.newsletterForm.getRawValue().name
      }" created successfully`
    );
    this.navigateToParentRouteWithQueryParam(firstRegion.region);
  }

  onCreateError() {
    this.alertService.error(
      `Newsletter: "${this.newsletterForm.getRawValue()}" was not created, please try again.`
    );
  }

  navigateToParentRouteWithQueryParam(site: string) {
    this.router.navigate(['./'], {
      relativeTo: this.route.parent,
      queryParams: { site },
    });
  }
}
